import { guardPreview } from '@/lib/previewMode'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { portalApi } from '@/api/portal'
import { ArrowLeft, Printer, CreditCard, CheckCircle2 } from 'lucide-react'
import { formatMoney } from '@/lib/money'
import { usePortalDates } from '@/hooks/useDates'
import { formatAddress } from '@/lib/address'

const paymentLabel: Record<string, string> = {
  Unpaid: 'Unpaid',
  PartiallyPaid: 'Partially paid',
  Paid: 'Paid',
  Void: 'Void',
}

const paymentColour: Record<string, string> = {
  Unpaid: 'text-red-700 bg-red-50',
  PartiallyPaid: 'text-yellow-700 bg-yellow-50',
  Paid: 'text-green-700 bg-green-50',
  Void: 'text-gray-500 bg-gray-100',
}

export default function PortalInvoicePage() {
  const { formatDate } = usePortalDates()
  const fmt = (date: string) => {
    return formatDate(date + (date.includes('T') ? '' : 'T00:00:00'), { day: 'numeric', month: 'long', year: 'numeric' })
  }
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const paymentSuccess = searchParams.get('payment') === 'success'

  const { data: inv, isLoading } = useQuery({
    queryKey: ['portal-invoice', id],
    queryFn: () => portalApi.getInvoice(id!).then(r => r.data),
    enabled: !!id,
  })

  const checkoutMutation = useMutation({
    mutationFn: () => portalApi.createInvoiceCheckout(id!).then(r => r.data),
    onSuccess: ({ url }) => { window.location.href = url },
  })

  if (isLoading) return <p className="text-gray-500 p-6">Loading…</p>
  if (!inv) return <p className="text-gray-500 p-6">Invoice not found.</p>

  const companyAddress = formatAddress({
    line1: inv.companyAddressLine1, line2: inv.companyAddressLine2, city: inv.companyCity,
    county: inv.companyCounty, postcode: inv.companyPostcode, country: inv.companyCountry,
  }, inv.region)

  const clientAddress = formatAddress({
    line1: inv.clientAddressLine1, line2: inv.clientAddressLine2, city: inv.clientCity,
    county: inv.clientCounty, postcode: inv.clientPostcode, country: inv.clientCountry,
  }, inv.region, '\n')

  const cls = paymentColour[inv.paymentStatus] ?? 'text-gray-500 bg-gray-100'
  const label = paymentLabel[inv.paymentStatus] ?? inv.paymentStatus

  return (
    <div>
      {/* ── Screen-only toolbar ── */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <ArrowLeft size={15} /> Back
        </button>
        <div className="flex items-center gap-2">
          {inv && inv.paymentStatus === 'Unpaid' && inv.stripeConnectEnabled && (
            <button
              onClick={() => { if (!guardPreview()) checkoutMutation.mutate() }}
              disabled={checkoutMutation.isPending}
              className="flex items-center gap-1.5 text-sm font-medium text-[var(--brand-primary-contrast)] bg-[var(--brand-primary)] hover:opacity-90 disabled:opacity-50 rounded-[var(--brand-radius-sm)] px-3 py-1.5 transition-opacity cursor-pointer"
            >
              <CreditCard size={15} />
              {checkoutMutation.isPending ? 'Redirecting…' : 'Pay securely with card'}
            </button>
          )}
          {inv && inv.paymentStatus === 'Unpaid' && !inv.stripeConnectEnabled && inv.companyPaymentLink && (
            <a
              href={inv.companyPaymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm font-medium text-[var(--brand-primary-contrast)] bg-[var(--brand-primary)] hover:opacity-90 rounded-[var(--brand-radius-sm)] px-3 py-1.5 transition-opacity cursor-pointer"
            >
              <CreditCard size={15} /> Pay now
            </a>
          )}
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <Printer size={15} /> Print
          </button>
        </div>
      </div>

      {paymentSuccess && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 print:hidden">
          <CheckCircle2 size={16} className="text-green-600" />
          Payment received — thank you! Your invoice will be marked as paid shortly.
        </div>
      )}

      {/* ── Invoice document ── */}
      <style>{`@page { size: A4; margin: 0; }`}</style>
      <div className="bg-slate-100 -mx-10 -mb-10 px-10 py-10 print:bg-transparent print:p-0 print:m-0">
      <div className="bg-white w-[210mm] min-h-[297mm] mx-auto shadow-md p-[15mm] print:shadow-none print:p-[15mm]">

        {/* Header: company on left, INVOICE on right */}
        <div className="flex justify-between items-start mb-10">
          <div className="flex flex-col gap-3">
            {inv.companyLogoUrl && (
              <img
                src={inv.companyLogoUrl}
                alt={inv.companyDisplayName ?? 'Company logo'}
                className="h-24 w-auto object-contain object-left"
              />
            )}
            <div>
              {inv.companyDisplayName && (
                <p className="font-bold text-gray-900 text-lg">{inv.companyDisplayName}</p>
              )}
              {companyAddress && (
                <p className="text-sm text-gray-500 mt-0.5">{companyAddress}</p>
              )}
              {inv.companyEmail && (
                <p className="text-sm text-gray-500">{inv.companyEmail}</p>
              )}
              {inv.companyWebsite && (
                <p className="text-sm text-gray-500">{inv.companyWebsite}</p>
              )}
              {inv.companyVatNumber && (
                <p className="text-sm text-gray-500">{inv.taxLabel}: {inv.companyVatNumber}</p>
              )}
              {inv.companyNumber && (
                <p className="text-sm text-gray-500">Co. No: {inv.companyNumber}</p>
              )}
            </div>
          </div>

          <div className="text-right">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">INVOICE</h1>
            <p className="text-gray-500 font-mono text-sm mt-1">{inv.invoiceNumber}</p>
            <div className={`inline-flex mt-2 rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
              {label}
            </div>
          </div>
        </div>

        {/* Invoice meta + Bill To */}
        <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 sm:gap-8">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Bill to</p>
            <p className="font-semibold text-gray-900">{inv.clientName}</p>
            {inv.clientContactName && (
              <p className="text-sm text-gray-600">{inv.clientContactName}</p>
            )}
            <p className="text-sm text-gray-600">{inv.clientContactEmail}</p>
            {clientAddress && (
              <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{clientAddress}</p>
            )}
          </div>

          <div className="text-right">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Invoice details</p>
            <table className="ml-auto text-sm">
              <tbody>
                <tr>
                  <td className="text-gray-500 pr-6 py-0.5">Issue date</td>
                  <td className="font-medium text-gray-900">{fmt(inv.issuedDate)}</td>
                </tr>
                {inv.dueDate && (
                  <tr>
                    <td className="text-gray-500 pr-6 py-0.5">Due date</td>
                    <td className="font-medium text-gray-900">{fmt(inv.dueDate)}</td>
                  </tr>
                )}
                {inv.lines.length === 1 && (
                  <tr>
                    <td className="text-gray-500 pr-6 py-0.5">Training date</td>
                    <td className="font-medium text-gray-900">{fmt(inv.lines[0].bookingDate)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Line items */}
        <table className="w-full text-sm mb-6">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">Description</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 w-20">Qty</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 w-28">Unit price</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map(line => (
              <tr key={line.bookingId} className="border-b border-gray-100">
                <td className="py-4">
                  <p className="font-medium text-gray-900">
                    {line.courseName}
                    {line.pricingModel === 'FlatFee' && (
                      <span className="text-gray-400 font-normal">
                        {' '}(flat fee{line.flatFeeCapacity != null ? `, up to ${line.flatFeeCapacity} people` : ''})
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {fmt(line.bookingDate)}
                    {line.courseCode && <span className="font-mono">{' · ' + line.courseCode}</span>}
                  </p>
                </td>
                <td className="text-right text-gray-700 py-4">{line.places}</td>
                <td className="text-right text-gray-700 py-4">
                  {line.pricingModel === 'FlatFee' ? '—' : line.unitPrice != null ? formatMoney(line.unitPrice, inv.currency) : '—'}
                </td>
                <td className="text-right font-medium text-gray-900 py-4">{formatMoney(line.amount, inv.currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {inv.vatAmount != null && inv.vatAmount > 0 ? (
              <>
                <tr>
                  <td colSpan={2} />
                  <td className="text-right text-sm text-gray-500 pt-4 pr-0">Subtotal</td>
                  <td className="text-right font-medium text-gray-900 pt-4">{formatMoney(inv.amount, inv.currency)}</td>
                </tr>
                <tr>
                  <td colSpan={2} />
                  <td className="text-right text-sm text-gray-500 pt-1 pr-0">{inv.taxLabel} ({inv.vatRate}%)</td>
                  <td className="text-right font-medium text-gray-900 pt-1">{formatMoney(inv.vatAmount, inv.currency)}</td>
                </tr>
                <tr>
                  <td colSpan={2} />
                  <td className="text-right text-xl font-bold text-gray-900 pt-2 border-t-2 border-gray-900 pr-0">Total</td>
                  <td className="text-right text-xl font-bold text-gray-900 pt-2 border-t-2 border-gray-900">{formatMoney(inv.total, inv.currency)}</td>
                </tr>
              </>
            ) : (
              <>
                <tr>
                  <td colSpan={2} />
                  <td className="text-right text-sm text-gray-500 pt-4 pr-0">Subtotal</td>
                  <td className="text-right font-medium text-gray-900 pt-4">{formatMoney(inv.amount, inv.currency)}</td>
                </tr>
                <tr>
                  <td colSpan={2} />
                  <td className="text-right text-xl font-bold text-gray-900 pt-2 border-t-2 border-gray-900 pr-0">Total</td>
                  <td className="text-right text-xl font-bold text-gray-900 pt-2 border-t-2 border-gray-900">{formatMoney(inv.total, inv.currency)}</td>
                </tr>
              </>
            )}
          </tfoot>
        </table>

        {/* Notes */}
        {inv.notes && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Notes</p>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{inv.notes}</p>
          </div>
        )}

        {/* Payment details */}
        {(inv.companyBankName || inv.companyAccountNumber || inv.companyIban) && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Payment details</p>
            <div className="grid gap-x-8 gap-y-1.5 text-sm" style={{ gridTemplateColumns: 'auto 1fr' }}>
              {inv.companyBankName && (
                <>
                  <span className="text-gray-500">Bank</span>
                  <span className="text-gray-900 font-medium">{inv.companyBankName}</span>
                </>
              )}
              {inv.companyAccountName && (
                <>
                  <span className="text-gray-500">Account name</span>
                  <span className="text-gray-900 font-medium">{inv.companyAccountName}</span>
                </>
              )}
              {inv.companyAccountNumber && (
                <>
                  <span className="text-gray-500">Account number</span>
                  <span className="text-gray-900 font-mono font-medium">{inv.companyAccountNumber}</span>
                </>
              )}
              {inv.companySortCode && (
                <>
                  <span className="text-gray-500">Sort code</span>
                  <span className="text-gray-900 font-mono font-medium">{inv.companySortCode}</span>
                </>
              )}
              {inv.companyRoutingNumber && (
                <>
                  <span className="text-gray-500">Routing number (ABA)</span>
                  <span className="text-gray-900 font-mono font-medium">{inv.companyRoutingNumber}</span>
                </>
              )}
              {inv.companyIban && (
                <>
                  <span className="text-gray-500">IBAN</span>
                  <span className="text-gray-900 font-mono font-medium">{inv.companyIban}</span>
                </>
              )}
            </div>
            {inv.companyPaymentReference && (
              <p className="text-sm text-gray-500 mt-3">{inv.companyPaymentReference}</p>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  )
}
